import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, Loader2, Trash2, FileText, FileSpreadsheet, TrendingUp, TrendingDown, Users2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";
import { money, CAT_CLS, downloadDoc } from "./shared";
import { useI18n } from "../../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];

export default function Facturas({ companyId, year, month, onDataChange }) {
  const { t } = useI18n();
  const [category, setCategory] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!companyId) return;
    const p = new URLSearchParams({ company_id: companyId });
    if (year) p.append("year", year);
    if (month) p.append("month", month);
    if (category) p.append("category", category);
    api.get(`/accounting/invoices?${p}`).then((r) => setInvoices(r.data)).catch(() => {});
    const sp = new URLSearchParams({ company_id: companyId });
    if (year) sp.append("year", year);
    if (month) sp.append("month", month);
    api.get(`/accounting/summary?${sp}`).then((r) => setSummary(r.data)).catch(() => {});
  }, [companyId, year, month, category]);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0, dup = 0;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("company_id", companyId);
      fd.append("file", file);
      try { await api.post("/accounting/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (e) { if (e.response?.status === 409) dup++; else toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail) || "Error"}`); }
    }
    setUploading(false);
    if (ok) toast.success(t("os.contab.invoicesUploaded")(ok));
    if (dup) toast(t("os.contab.duplicatesSkipped")(dup));
    if (fileRef.current) fileRef.current.value = "";
    onDataChange?.(); load();
  };
  const reclassify = async (id, cat) => { try { await api.patch(`/accounting/invoices/${id}`, { category: cat }); load(); } catch { toast.error(t("os.contab.err")); } };
  const del = async (id) => {
    if (!window.confirm(t("os.contab.delInvoiceConfirm"))) return;
    try { await api.delete(`/accounting/invoices/${id}`); toast.success(t("os.contab.deleted")); onDataChange?.(); load(); } catch { toast.error(t("os.contab.err")); }
  };

  const kpis = summary ? [
    { key: "ingreso", label: t("os.contab.kpi.ingreso"), value: summary.totals.ingreso, icon: TrendingUp, cls: "text-emerald-600" },
    { key: "egreso", label: t("os.contab.kpi.egreso"), value: summary.totals.egreso, icon: TrendingDown, cls: "text-destructive" },
    { key: "nomina", label: t("os.contab.kpi.nomina"), value: summary.totals.nomina, icon: Users2, cls: "text-violet" },
    { key: "utilidad", label: t("os.contab.kpi.utilidad"), value: summary.utilidad, icon: TrendingUp, cls: "text-navy" },
  ] : [];

  const CATS = [["", "all"], ["ingreso", "ingreso"], ["egreso", "egreso"], ["nomina", "nomina"], ["pago", "pago"]];
  const CAT_KEYS = ["ingreso", "egreso", "nomina", "pago", "otro"];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 bg-white border border-navy/10 rounded-lg p-1">
          {CATS.map(([v, k]) => (
            <button key={v} data-testid={`acct-cat-${v || "all"}`} onClick={() => setCategory(v)}
              className={`text-xs px-3 py-1.5 rounded-md transition-colors ${category === v ? "bg-navy text-cream" : "text-navy/50 hover:text-navy"}`}>{t(`os.contab.cats.${k}`)}</button>
          ))}
        </div>
        <input ref={fileRef} type="file" accept=".xml,.pdf" multiple hidden data-testid="acct-file-input" onChange={(e) => onFiles(e.target.files)} />
        <button data-testid="acct-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity disabled:opacity-60">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t("os.contab.uploadInvoices")}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <motion.div key={k.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease, delay: i * 0.05 }}
            data-testid={`acct-kpi-${k.key}`} className="bg-white border border-navy/10 rounded-lg p-5">
            <div className="flex items-center justify-between"><div className="overline text-navy/40 text-[0.6rem]">{k.label}</div><k.icon className={`w-4 h-4 ${k.cls}`} /></div>
            <div className={`mt-2 text-2xl font-medium tracking-tight ${k.cls}`}>{money(k.value)}</div>
          </motion.div>
        ))}
      </div>

      {summary && (
        <div className="bg-white border border-navy/10 rounded-lg p-5" data-testid="acct-iva-panel">
          <div className="overline text-navy/40 mb-3">{t("os.contab.ivaPanel")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-navy/40 text-xs">{t("os.contab.ivaTrasladado")}</div><div className="font-medium mt-1">{money(summary.iva.trasladado)}</div></div>
            <div><div className="text-navy/40 text-xs">{t("os.contab.ivaAcreditable")}</div><div className="font-medium mt-1">{money(summary.iva.acreditable)}</div></div>
            <div><div className="text-navy/40 text-xs">{t("os.contab.ivaPorPagar")}</div><div className={`font-medium mt-1 ${summary.iva.por_pagar >= 0 ? "text-destructive" : "text-emerald-600"}`}>{money(summary.iva.por_pagar)}</div></div>
            <div><div className="text-navy/40 text-xs">{t("os.contab.retenciones")}</div><div className="font-medium mt-1">{money(summary.iva.retenido + summary.isr_retenido)}</div></div>
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="text-navy/40 py-12 text-center border border-dashed border-navy/15 rounded-lg" data-testid="acct-empty">
          {t("os.contab.emptyInvoices")}
        </div>
      ) : (
        <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="acct-invoices-panel">
          <div className="hidden md:grid grid-cols-[90px_1.4fr_1fr_110px_130px_130px_60px] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10">
            <span>{t("os.contab.invCols.fecha")}</span><span>{t("os.contab.invCols.emisor")}</span><span>{t("os.contab.invCols.receptor")}</span><span>{t("os.contab.invCols.total")}</span><span>{t("os.contab.invCols.categoria")}</span><span>{t("os.contab.invCols.origen")}</span><span /></div>
          {invoices.map((inv) => (
            <div key={inv.id} data-testid={`acct-invoice-${inv.id}`} className="grid grid-cols-2 md:grid-cols-[90px_1.4fr_1fr_110px_130px_130px_60px] gap-3 px-5 py-4 items-center border-b border-navy/5 last:border-0 text-sm">
              <div className="text-xs text-navy/50">{(inv.fecha || "").slice(0, 10)}</div>
              <div className="min-w-0"><div className="font-medium truncate">{inv.emisor_nombre || "—"}</div><div className="font-mono text-[11px] text-navy/40">{inv.emisor_rfc}</div></div>
              <div className="min-w-0"><div className="truncate text-navy/70">{inv.receptor_nombre || "—"}</div><div className="font-mono text-[11px] text-navy/40">{inv.receptor_rfc}</div></div>
              <div className="font-medium">{money(inv.total)}</div>
              <div>
                <select data-testid={`acct-reclass-${inv.id}`} value={inv.category} onChange={(e) => reclassify(inv.id, e.target.value)}
                  className={`text-[11px] px-2 py-1 rounded-full outline-none cursor-pointer ${CAT_CLS[inv.category] || CAT_CLS.otro}`}>
                  {CAT_KEYS.map((v) => <option key={v} value={v}>{t(`os.contab.catLabels.${v}`)}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-navy/50">
                {inv.source === "xml" ? <FileSpreadsheet className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                {inv.source === "xml" ? t("os.contab.xmlCfdi") : t("os.contab.pdfIa")}
                {inv.from_project && <span data-testid={`acct-from-project-${inv.id}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet/10 text-violet">{t("os.contab.fromProject")}</span>}
              </div>
              <div className="flex items-center gap-2 justify-self-end">
                {inv.attachments?.length ? inv.attachments.map((a) => (
                  <button key={a.id} data-testid={`acct-download-${inv.id}-${a.kind}`} title={`${t("os.contab.downloadOriginal")} ${(a.kind || "").toUpperCase()}`}
                    onClick={() => downloadDoc("invoice", inv.id, a.filename, a.id)} className="text-navy/30 hover:text-violet">
                    {a.kind === "xml" ? <FileSpreadsheet className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                  </button>
                )) : (inv.storage_path && <button data-testid={`acct-download-${inv.id}`} title={t("os.contab.downloadOriginal")} onClick={() => downloadDoc("invoice", inv.id, inv.original_filename)} className="text-navy/30 hover:text-violet"><Download className="w-4 h-4" /></button>)}
                <button data-testid={`acct-del-${inv.id}`} onClick={() => del(inv.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
